import { CrudFilter, DataProvider, LogicalFilter } from "@refinedev/core";
import _ from "lodash";

import { transformFilter } from "@/lib/refineUtils";
import { Sentry } from "@/lib/sentry";
import { coalesce } from "@/lib/utils";
import { getFirebaseUser } from "@/providers/auth-provider";

const min = (a: number, b: number) => (a < b ? a : b);

const authAttached = new WeakSet<object>();

/**
 * Adds the Firebase Bearer interceptor to an axios client, once per client: the default
 * provider and `areasDataProvider` share one instance and must not stack two interceptors.
 */
export const attachFirebaseAuth = (client: any): void => {
  if (authAttached.has(client)) return;
  authAttached.add(client);
  client.interceptors.request.use(
    async (config: any) => {
      const user = await getFirebaseUser();
      const token = await user.getIdToken();
      if (token && config?.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error: any) => {
      return Promise.reject(error);
    },
  );
};

/**
 * Check out the Data Provider documentation for detailed information
 * https://refine.dev/docs/api-reference/core/providers/data-provider/
 **/
export const dataProvider = (
  apiUrl: string,
  _httpClient: any, // TODO: replace `any` with your http client type
): DataProvider => {
  attachFirebaseAuth(_httpClient);

  return {
    getList: async ({ resource, pagination, filters, sorters, meta }) => {
      const url = `${apiUrl}/${resource}/explore`;

      console.debug("getList", {
        resource,
        pagination,
        filters,
        sorters,
        meta,
        url,
      });

      // TODO: send request to the API
      const response = await _httpClient.get(url, {
        params: {
          q: coalesce(meta?.query, "*"),
          page: pagination?.currentPage ?? 1,
          limit: pagination?.pageSize || 20,
          filters: filters
            ?.map(transformFilter)
            .filter((f) => f)
            .join(" && "),
          sortBy:
            sorters
              ?.slice(0, min(3, sorters.length))
              .map((s) => `${s.field}:${s.order}`)
              .join(", ") ?? "",
          queryBy: coalesce(meta?.queryBy, meta?.defaultQueryBy ?? null),
          "api-version": "1.0",
          facetBy: meta?.facetBy?.join(", ") || null,
          facetReturnParent: meta?.facetReturnParent?.join(", ") || null,
          includeFields: meta?.includeFields?.join(",") || null,
          pinnedHits:
            meta?.pinnedHits
              ?.map((x: string, idx: number) => `${x}:${idx + 1}`)
              .join(",") || null,
          facetQuery: coalesce(
            meta?.facetQueryBy
              ? meta?.facetQueryBy + ":" + meta?.facetQuery
              : undefined,
            null,
          ),
          disableCaching: true,
        },
      });
      console.debug(response);
      // const response = await httpClient.get(url, {});

      return {
        data: response.data.hits ?? response.data,
        total: response.data.found ?? 0,
        facets: response.data.facet_counts ?? [],
      };
    },

    getMany: async ({ resource, ids, meta }) => {
      console.debug("getMany", {
        resource,
        ids,
        meta,
      });

      // TODO: send request to the API
      // const response = await httpClient.get(url, {});

      return {
        data: [],
      };
    },

    create: async ({ resource, variables, meta }) => {
      const url = `${apiUrl}/${resource}`;

      console.debug("create", { url, resource, variables, meta });
      Sentry.addBreadcrumb({
        category: `${resource}.create`,
        message: `Creating ${resource}`,
        level: "info",
      });

      try {
        const response = await _httpClient.post(url, variables, {
          params: { "api-version": "1.0" },
        });
        console.debug("[Create Response]", response);
        Sentry.addBreadcrumb({
          category: `${resource}.create`,
          message: `Created ${resource} successfully`,
          level: "info",
          data: { id: response?.data?.id },
        });
        return response?.data;
      } catch (error) {
        Sentry.addBreadcrumb({
          category: `${resource}.create`,
          message: `Failed to create ${resource}`,
          level: "error",
        });
        Sentry.captureException(error);
        throw error;
      }
    },

    update: async ({ resource, id, variables, meta }) => {
      const url = `${apiUrl}/${resource}`;

      console.debug("update", { resource, id, variables, meta });
      Sentry.addBreadcrumb({
        category: `${resource}.update`,
        message: `Updating ${resource}`,
        level: "info",
        data: { id },
      });

      try {
        const response = await _httpClient.put(url, variables, {
          params: { "api-version": "1.0" },
        });
        console.debug("[Update Response]", response);
        Sentry.addBreadcrumb({
          category: `${resource}.update`,
          message: `Updated ${resource} successfully`,
          level: "info",
          data: { id },
        });
        return { data: response.data };
      } catch (error) {
        Sentry.addBreadcrumb({
          category: `${resource}.update`,
          message: `Failed to update ${resource}`,
          level: "error",
          data: { id },
        });
        Sentry.captureException(error);
        throw error;
      }
    },

    getOne: async ({ resource, id, meta }) => {
      const url = `${apiUrl}/${resource}/${id}`;

      console.debug("getOne", { resource, id, meta });

      try {
        const response = await _httpClient.get(url, {
          params: { "api-version": "1.0" },
        });
        console.debug("[GetOne Response]", response.data);
        return response.data;
      } catch (error) {
        Sentry.addBreadcrumb({
          category: `${resource}.getOne`,
          message: `Failed to fetch ${resource}`,
          level: "error",
          data: { id },
        });
        Sentry.captureException(error);
        throw error;
      }
    },

    deleteOne: async ({ resource, id, variables, meta }) => {
      const url = `${apiUrl}/${resource}`;

      console.debug("deleteOne", { resource, id, variables, meta });
      Sentry.addBreadcrumb({
        category: `${resource}.delete`,
        message: `Deleting ${resource}`,
        level: "info",
        data: { id },
      });

      try {
        const response = await _httpClient.delete(url, {
          params: { "api-version": "1.0" },
          data: { id },
        });
        console.debug("[Delete Response]", response);
        return { data: {} as any };
      } catch (error) {
        Sentry.addBreadcrumb({
          category: `${resource}.delete`,
          message: `Failed to delete ${resource}`,
          level: "error",
          data: { id },
        });
        Sentry.captureException(error);
        throw error;
      }
    },

    getApiUrl: () => {
      return apiUrl;
    },

    custom: async ({
      url,
      method,
      filters,
      sorters,
      payload,
      query,
      headers,
      meta,
    }) => {
      console.debug("custom", {
        url,
        method,
        filters,
        sorters,
        payload,
        query,
        headers,
        meta,
      });

      const response = await _httpClient({
        method,
        url: `${_.isEmpty(url) ? apiUrl : url}/${meta?.urlPath || ""}`,
        params: {
          "api-version": "1.0",
          ...query,
        },
        // body: payload,
        headers: headers,
        data: payload,
        ...meta,
      });

      console.debug("[Custom Response]", response);

      return response;
    },
  };
};
